// ════════════════════════════════════════════════════════════
//  Online.jsx — the account chip, sign-in, the Online hub, a friend's
//  room, THE HOURLY board and the betting panel
// ════════════════════════════════════════════════════════════
//  Plain DOM, same look as the other screens. Every number and rule
//  comes from online/*.js; this file only shows them and calls them.
// ════════════════════════════════════════════════════════════
import { useEffect, useMemo, useRef, useState } from "react";
import { LINES, portraitUrl } from "../data/roster.js";
import { fbNet as net } from "../online/net.js";
import { fmtDust, fmtKrests, serverNow, MINI_DUST } from "../online/ie.js";
import { placeBet, poolTotals, BET_MIN, BET_MAX, collect } from "../online/bets.js";
import {
  createRoom, joinRoom, leaveRoom, setupRoomMatch, startRoomMatch, roomPath, matchPath,
  CAST_TEAMS, poolResultOf, ROOM_COUNTDOWN,
} from "../online/rooms.js";
import { hourOf, hourStart, hourInfo, GENESIS_MS } from "../online/circuit.js";
import { BASE } from "../online/ie.js";
import Spread from "./Spread.jsx";

// Fights this tab has already been sent to. Module-level ON PURPOSE: a room or
// board that mounts again must not bounce you straight back into a fight you
// just left.
export const SENT = new Set();
export const CALLED_BY = { beings: "called: more beings standing", anima: "called: more Anima left", resurrections: "called: fewer resurrections", lyf: "called: more Lyf left", coin: "called: dead even, the coin decided" };

const clock = (ms) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
const localTime = (ms) => new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const useTick = (ms = 1000) => { const [, set] = useState(0); useEffect(() => { const id = setInterval(() => set((n) => n + 1), ms); return () => clearInterval(id); }, [ms]); };
const useLive = (path) => {
  const [v, setV] = useState(undefined);
  useEffect(() => { if (!path) { setV(null); return undefined; } setV(undefined); return net.onValue(path, setV); }, [path]);
  return v;
};

function Faces({ team, tier = "beb" }) {
  return (
    <span className="on-faces">
      {(team || []).map((id, i) => LINES[id] ? <i key={i} title={LINES[id].lord} style={{ backgroundImage: `url(${portraitUrl(id, tier)})` }} /> : null)}
    </span>
  );
}

// ── who you are, top right ───────────────────────────────────
export function AccountChip({ on, onOpen }) {
  if (on.phase === "boot") return <div className="on-chip dim">…</div>;
  if (!on.writable) return <button className="on-chip" onClick={onOpen}>{on.isGuest ? `${on.name} · sign in to bet` : "Sign in"}</button>;
  return (
    <button className="on-chip" onClick={onOpen} title={`Server ${on.server}`}>
      <b>{on.name}</b><span className="dust">{on.dust == null ? "…" : fmtDust(on.dust)}</span>{on.krests > 0 && <span className="krests" title="King's Krests">{fmtKrests(on.krests)}</span>}<span className="srv">S{on.server}</span>
    </button>
  );
}

// ── signing in (the Empire's one soul) ───────────────────────
export function SignIn({ on, onDone, onBack }) {
  const [mode, setMode] = useState("in");
  const [email, setEmail] = useState(on.guestEmail || "");
  const [pw, setPw] = useState("");
  const [name, setName] = useState("");
  useEffect(() => { if (on.writable && on.phase === "ready") onDone(); }, [on.writable, on.phase, onDone]);
  const go = async (e) => {
    e.preventDefault();
    if (mode === "in") await on.signIn(email, pw);
    else await on.signUp(email, pw, name);
  };
  if (on.phase === "name") {
    return (
      <div className="screen online">
        <form className="panel on-panel" onSubmit={(e) => { e.preventDefault(); on.claimName(name); }}>
          <h2>Pick your name</h2>
          <input className="on-input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" maxLength={24} />
          <div className="title-buttons"><button className="btn primary" type="submit">That's me</button></div>
        </form>
      </div>
    );
  }
  return (
    <div className="screen online">
      <form className="panel on-panel" onSubmit={go}>
        <h2>{mode === "in" ? "Sign in" : "Make your soul"}</h2>
        <p className="on-note">One account for the whole Empire — the same one as the Pier and Terror.</p>
        <input className="on-input" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" />
        <input className="on-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" />
        {mode === "up" && <input className="on-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" maxLength={24} />}
        {on.error && <div className="on-err">{on.error}</div>}
        <div className="title-buttons">
          <button className="btn primary" type="submit" disabled={on.busy}>{mode === "in" ? "Sign in" : "Create"}</button>
          <button className="btn ghost" type="button" onClick={() => { on.setError(null); setMode(mode === "in" ? "up" : "in"); }}>{mode === "in" ? "New here?" : "I have one"}</button>
          <button className="btn ghost" type="button" onClick={onBack}>Back</button>
        </div>
      </form>
    </div>
  );
}

// ── collecting winnings (any screen can call it) ─────────────
// A Circuit fight nobody watched gets its spec and result from the keeper
// (ui/Circuit.jsx useCircuitKeeper); until then its pool simply waits.
const resultOfAny = (poolPath) => poolResultOf(net)(poolPath);
export function useCollector(on, onPaid) {
  const busy = useRef(false);
  const run = useMemo(() => async () => {
    if (!on.writable || !on.authUid || busy.current) return;
    busy.current = true;
    try { const paid = await collect(net, on.authUid, resultOfAny, serverNow); if (paid.length && onPaid) onPaid(paid); }
    catch (e) { console.warn("[shatterproof] collecting bets", e); } // eslint-disable-line no-console
    busy.current = false;
  }, [on.writable, on.authUid, onPaid]);
  useEffect(() => { run(); const id = setInterval(run, 20000); return () => clearInterval(id); }, [run]);
  return run;
}

// ── THE BETTING PANEL ────────────────────────────────────────
export function BetPanel({ on, path, pool = null, names, lockAt, keys = null, open = true, fighters = [] }) {
  useTick(500);
  const poolPath = pool || (path ? `${path}/bets` : null);
  const bets = useLive(poolPath) || {};
  const [side, setSide] = useState(null);
  const [amt, setAmt] = useState(BET_MIN);
  const [msg, setMsg] = useState(null);
  const [showOdds, setShowOdds] = useState(false);
  const { total, count } = poolTotals(bets);
  const mine = on.authUid ? bets[on.authUid] : null;
  const closed = !open || (lockAt != null && serverNow() >= lockAt);
  const fighting = on.authUid && fighters.includes(on.authUid);
  const place = async () => {
    setMsg("…");
    const r = await placeBet(net, { poolPath, uid: on.authUid, name: on.name, side, amount: amt, lockAt, now: serverNow, keys, fighters })
      .catch(() => ({ ok: false, reason: "Something went wrong — check your Mini Dust and try again." }));
    setMsg(r.ok ? "You're in. Good luck." : r.reason);
  };
  const odds = (s) => (total[s] ? (1 + total[1 - s] / total[s]).toFixed(2) : "—");
  return (
    <div className="on-bet">
      <div className="on-bet-head">Bet {MINI_DUST.name} <small>players vs players · no house</small>
        {keys && <button className="btn tiny ghost on-odds-btn" onClick={() => setShowOdds((o) => !o)}>{showOdds ? "Hide odds" : "Odds"}</button>}</div>
      {showOdds && keys && <Spread keys={keys} names={names} total={total} />}
      <div className="on-bet-sides">
        {[0, 1].map((s) => (
          <button key={s} className={"on-bet-side s" + s + (side === s ? " on" : "") + (mine && mine.side === s ? " mine" : "")} disabled={closed || !!mine} onClick={() => setSide(s)}>
            <b>{names[s]}</b>
            <span>{fmtDust(total[s])} · {count[s]} bet{count[s] === 1 ? "" : "s"} · pays ×{odds(s)}</span>
          </button>
        ))}
      </div>
      {mine ? (
        <div className="on-note">Your bet: {fmtDust(mine.amount)} on <b>{names[mine.side]}</b>{mine.paid ? ` · collected ${fmtDust(mine.claimed?.owed ?? 0)}` : ""}</div>
      ) : closed ? (
        <div className="on-note">{lockAt != null && serverNow() >= lockAt ? "Betting closed at the bell." : "Betting opens once the fight is set up."}</div>
      ) : !on.writable ? (
        <div className="on-note">Sign in to bet.</div>
      ) : fighting ? (
        <div className="on-note">You're in this fight — no betting on yourself.</div>
      ) : (
        <div className="on-bet-row">
          {BET_MAX > BET_MIN && <input className="on-input small" type="number" min={BET_MIN} max={BET_MAX} value={amt} onChange={(e) => setAmt(e.target.value)} />}
          <button className="btn primary" disabled={side == null} onClick={place}>{side == null ? "Pick a side" : `Bet ${fmtDust(BET_MAX > BET_MIN ? amt : BET_MIN)} on ${names[side]}`}</button>
          {lockAt != null && <span className="on-note">closes in {clock(lockAt - serverNow())}</span>}
        </div>
      )}
      {msg && <div className="on-note">{msg}</div>}
    </div>
  );
}

// ── THE ONLINE HUB ───────────────────────────────────────────
export function OnlineHub({ on, onRoom, onHourly, onSignIn, onBack, toast }) {
  useTick(1000);
  const [code, setCode] = useState("");
  const [err, setErr] = useState(null);
  const partyRoom = useLive(on.partyId ? `${BASE}/partyRooms/${on.partyId}` : null);
  // the next big night: Season 1's start, then the next Main Event / cup / Apocalypse evening
  const BIG = ["main", "losers", "apocalypse", "winners", "finale", "kcup", "ccup", "champions"];   // V2 (2026-10-21): the nightly cups too
  let big = null, bigName = "";
  if (serverNow() < GENESIS_MS) { big = GENESIS_MS; bigName = "Season 1 begins"; }
  else for (let h = hourOf(serverNow()) + 1, n = 0; n < 48 && big == null; h++, n++) { const i = hourInfo(h); if (BIG.includes(i.kind)) { big = hourStart(h); bigName = i.name; } }
  const make = async () => {
    setErr(null);
    const r = await createRoom(net, { uid: on.authUid, name: on.name, server: on.server, partyId: on.partyId });
    if (r.ok) onRoom(r.code); else setErr(r.reason);
  };
  const join = async (c) => {
    setErr(null);
    const r = await joinRoom(net, { code: c, uid: on.authUid, name: on.name });
    if (r.ok) onRoom(r.code); else setErr(r.reason);
  };
  void toast;
  return (
    <div className="screen online">
      <div className="on-top"><button className="btn ghost" onClick={onBack}>← Title</button><h2>The League</h2><span /></div>
      <div className="on-cards">
        <div className="panel on-card">
          <h3>Watch the League</h3>
          <p className="on-note">One fight at a time, all day. Follow the table, catch the Main Event at 9.</p>
          <div className="on-big">{big - serverNow() >= 3600e3 ? `${Math.floor((big - serverNow()) / 3600e3)}h ${Math.floor(((big - serverNow()) % 3600e3) / 60e3)}m` : clock(big - serverNow())}</div>
          <p className="on-note">until {bigName.replace(/^The /, "the ")} · {localTime(big)}</p>
          {/* the Empire's database only answers signed-in souls, so watching needs a sign-in too */}
          <button className="btn primary" onClick={on.authUid ? onHourly : onSignIn}>{on.authUid ? "Open the League" : "Sign in to see the League"}</button>
        </div>
        <div className="panel on-card">
          <h3>Friends <small className="on-server">server {on.server}</small></h3>
          {!on.writable ? (
            <><p className="on-note">Sign in to make or join a room.</p><button className="btn primary" onClick={onSignIn}>Sign in</button></>
          ) : (
            <>
              <p className="on-note">Make a room, share the 4-letter code. Watch two NPCs fight together, or fight each other — and bet either way.</p>
              <button className="btn primary" onClick={make}>Make a room</button>
              <div className="on-bet-row">
                <input className="on-input small code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CODE" maxLength={4} />
                <button className="btn" onClick={() => join(code)}>Join</button>
              </div>
              {partyRoom && <button className="btn" onClick={() => join(partyRoom)}>Your party is in room {partyRoom} — join</button>}
            </>
          )}
          {err && <div className="on-err">{err}</div>}
        </div>
      </div>
    </div>
  );
}

// ── A FRIEND'S ROOM ──────────────────────────────────────────
export function RoomScreen({ on, code, team, tomes, onPickTeam, onWatch, onBack }) {
  useTick(500);
  const room = useLive(roomPath(code));
  const spec = useLive(room && room.match ? `${matchPath(room.match)}/spec` : null);
  const result = useLive(room && room.match ? `${matchPath(room.match)}/result` : null);
  const [err, setErr] = useState(null);
  const me = on.authUid;
  const isHost = room && room.host === me;
  // keep my team on my seat (the host reads it when they set up a fight)
  useEffect(() => {
    if (!room || !me || !room.members?.[me]) return;
    const mine = room.members[me];
    if (team && team.length === 3 && (JSON.stringify(mine.team) !== JSON.stringify(team) || JSON.stringify(mine.tomes || null) !== JSON.stringify(tomes || null))) net.update(`${roomPath(code)}/members/${me}`, { team, tomes: tomes || null }).catch(() => {});
  }, [room, me, team, tomes, code]);
  // leaving: my seat goes when my connection does
  useEffect(() => { if (me) net.onDisconnectRemove(`${roomPath(code)}/members/${me}`); }, [code, me]);
  // LYRAEI PLAYS (2026-10-17): where I am, so my partner can find me ("joinPartner").
  // games/shatterproof/inRoom/{uid} = code, gone when I leave or my connection drops.
  const gone = room === null;   // a room that vanished: the pointer goes too (review 2026-10-17 — she kept retrying a dead code)
  useEffect(() => {
    if (!me || gone) return undefined;
    const p = `${BASE}/inRoom/${me}`;
    net.set(p, code).catch(() => {});
    net.onDisconnectRemove(p);
    // a plain remove, not a txn: only my own screen writes my pointer, and one client's writes land in order
    // (a txn here would hit PRIME §2d's local-null trap and never clear it)
    return () => { net.remove(p).catch(() => {}); };
  }, [code, me, gone]);
  // a host whose connection dropped leaves the room with nobody in charge:
  // the oldest member still here takes over (a transaction, so only one does)
  useEffect(() => {
    if (!room || !room.members || room.members[room.host] || !me || !room.members[me]) return;
    net.txn(roomPath(code), (cur) => {
      if (!cur || !cur.members || cur.members[cur.host]) return undefined;
      const next = Object.entries(cur.members).sort((a, b) => (a[1].at || 0) - (b[1].at || 0))[0];
      return next ? { ...cur, host: next[0], rival: cur.rival === next[0] ? null : cur.rival } : undefined;
    }).catch(() => {});
  }, [room, me, code]);
  // the bell: everyone in the room goes to the fight together (once per fight, ever)
  useEffect(() => {
    if (spec && spec.startAt && !spec.void && room?.match && !SENT.has(room.match) && !result) {
      SENT.add(room.match);
      onWatch(room.match);
    }
  }, [spec, room, result, onWatch]);
  if (room === undefined) return <div className="screen online"><div className="on-note">Finding room {code}…</div></div>;
  if (room === null) return <div className="screen online"><div className="panel on-panel"><h2>Room {code} is gone</h2><button className="btn" onClick={onBack}>Back</button></div></div>;
  const members = Object.entries(room.members || {}).sort((a, b) => (a[1].at || 0) - (b[1].at || 0));
  const set = (patch) => net.update(roomPath(code), patch);
  const setup = async () => { setErr(null); const r = await setupRoomMatch(net, { code, room }); if (!r.ok) setErr(r.reason); };
  const start = async () => { setErr(null); const r = await startRoomMatch(net, { room }); if (!r.ok) setErr(r.reason); };
  const leave = async () => { await leaveRoom(net, { code, uid: me, room }); onBack(); };
  const pending = spec && !spec.void && !spec.startAt;
  const castOpt = CAST_TEAMS.map((c) => <option key={c.n} value={c.n}>{c.name}</option>);
  return (
    <div className="screen online">
      <div className="on-top"><button className="btn ghost" onClick={leave}>← Leave room</button><h2>Room <span className="on-code">{code}</span></h2><span /></div>
      <div className="on-cards">
        <div className="panel on-card">
          <h3>Who's here</h3>
          {members.map(([uid, m]) => (
            <div key={uid} className="on-member">
              <b>{m.name}</b>{uid === room.host && <small> · host</small>}{room.mode === "play" && uid === room.rival && <small> · fighting</small>}
              <Faces team={m.team} />
              {isHost && room.mode === "play" && uid !== me && <button className="btn ghost tiny" onClick={() => set({ rival: uid })}>Fight them</button>}
            </div>
          ))}
          <button className="btn ghost" onClick={onPickTeam}>My three beings…</button>
        </div>
        <div className="panel on-card">
          <h3>The fight</h3>
          {isHost ? (
            <>
              <div className="on-bet-row">
                <button className={"btn" + (room.mode === "watch" ? " primary" : "")} onClick={() => set({ mode: "watch" })}>Watch NPCs</button>
                <button className={"btn" + (room.mode === "play" ? " primary" : "")} onClick={() => set({ mode: "play" })}>Fight a friend</button>
              </div>
              {room.mode === "watch" ? (
                <div className="on-bet-row">
                  <select className="on-input" value={room.blue} onChange={(e) => set({ blue: +e.target.value })}>{castOpt}<option value={0}>Random team</option></select>
                  <span>vs</span>
                  <select className="on-input" value={room.red} onChange={(e) => set({ red: +e.target.value })}>{castOpt}<option value={0}>Random team</option></select>
                </div>
              ) : (
                <p className="on-note">{room.rival && room.members?.[room.rival] ? `You vs ${room.members[room.rival].name}. Everyone else watches.` : "Pick who you're fighting (\"Fight them\" beside a name)."}</p>
              )}
              <div className="on-bet-row">
                <button className="btn" onClick={setup}>{pending ? "Set it up again" : "Set it up (bets open)"}</button>
                <button className="btn primary" disabled={!pending} onClick={start}>Start ({ROOM_COUNTDOWN}s to the bell)</button>
              </div>
            </>
          ) : (
            <p className="on-note">{room.members?.[room.host]?.name || "The host"} sets the fight up. {room.mode === "play" ? (room.rival === me ? "You're fighting!" : "You're watching.") : "Everyone watches."}</p>
          )}
          {spec && !spec.void && (
            <div className="on-matchup">
              <div><Faces team={spec.teams[0]} /><b>{spec.names[0]}</b></div><span>vs</span><div><b>{spec.names[1]}</b><Faces team={spec.teams[1]} /></div>
            </div>
          )}
          {spec && !spec.void && <BetPanel on={on} path={matchPath(room.match)} names={spec.names} lockAt={spec.startAt ?? null} open={!!pending || !!spec.startAt}
            fighters={Object.values(spec.humans || {}).filter(Boolean)} />}
          {result && spec && <div className="on-note">Last fight: <b>{spec.names[result.winner]}</b> won ({result.why}).</div>}
          {spec && spec.startAt && !result && <button className="btn" onClick={() => { SENT.add(room.match); onWatch(room.match); }}>Back to the fight</button>}
          {err && <div className="on-err">{err}</div>}
        </div>
      </div>
    </div>
  );
}

// ── after an online fight ────────────────────────────────────
export function NetResults({ b, onBack, backLabel }) {
  const d = b.driver, spec = d.spec, my = d.mySide;
  const w = b.winner;
  const title = w == null ? "IT'S OVER" : my == null ? `${spec.names[w]} wins` : w === my ? "VICTORY" : "DEFEAT";
  const endEv = b.events.find((e) => e.type === "end");
  return (
    <div className="net-results">
      <div className={"res-banner " + (my == null ? "watch" : w === my ? "win" : "lose")}>{title}<small>{endEv && endEv.why === "called" ? (CALLED_BY[endEv.by] || "the duel was called") : endEv && endEv.why === "forfeit" ? "a no-show — forfeit" : "every being was ruined"}</small></div>
      {d.state.desync && <div className="on-err">⚠ This screen drifted out of sync with the others (a different browser engine?). The official result is the one everyone else saw.</div>}
      <div className="on-note">Bets on it settle by themselves — winnings land in your Mini Dust in a moment.</div>
      <div className="title-buttons"><button className="btn primary" onClick={onBack}>{backLabel}</button></div>
    </div>
  );
}
